import { useCallback, useEffect, useState } from 'react';
import type { AuditEvent } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { errorText } from './useRelay';

interface AuditState {
  events: AuditEvent[] | null;
  /** Cursor for the next (older) page; null when everything is loaded. */
  nextBefore: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * GET /audit for admins (it is admin-only): the newest `pageSize` events on open and on
 * `reload`, then `pageSize` older ones each time `more` is called.
 */
export function useAudit(enabled: boolean, pageSize: number) {
  const { api } = useAuth();
  const [state, setState] = useState<AuditState>({ events: null, nextBefore: null, loading: enabled, error: null });

  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const page = await api.listAudit({ limit: pageSize });
      setState({ events: page.events, nextBefore: page.nextBefore, loading: false, error: null });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: errorText(e) }));
    }
  }, [api, pageSize]);

  const more = useCallback(async () => {
    const before = state.nextBefore;
    if (!before) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const page = await api.listAudit({ limit: pageSize, before });
      setState((s) => ({
        events: [...(s.events ?? []), ...page.events],
        nextBefore: page.nextBefore,
        loading: false,
        error: null,
      }));
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: errorText(e) }));
    }
  }, [api, pageSize, state.nextBefore]);

  useEffect(() => {
    if (enabled) void reload();
  }, [enabled, reload]);

  return { ...state, reload, more };
}
